/**
 * DAC customization: timezone-aware open/closed status for store hours.
 * Used by the business hours bar hours block (settings-driven JSON).
 * Also marks the current day in the opening-hours dropdown.
 */

/**
 * @typedef {Object} StoreHoursPeriod
 * @property {number} openMinutes
 * @property {number} closeMinutes
 */

/**
 * @typedef {Object} StoreLocationData
 * @property {string} [name]
 * @property {string} [address]
 * @property {string} [phone]
 * @property {string} [directionsUrl]
 * @property {string} [timezone]
 * @property {string} [publicHolidayNote]
 * @property {Record<string, string | null | undefined>} [hours]
 * @property {Array<{date?: string, hours?: string}> | string} [specialHours]
 */

const DAY_KEYS = /** @type {const} */ ([
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
]);

const DAY_LABELS = /** @type {const} */ ([
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]);

/**
 * @param {string | null | undefined} raw
 * @returns {number | null}
 */
function parseTimeToMinutes(raw) {
  if (raw == null) return null;
  const value = String(raw).trim().toLowerCase();
  if (!value) return null;

  const match = value.match(/^(\d{1,2})(?::(\d{2}))?(?:\s*(am|pm))?$/i);
  if (!match) return null;

  let hours = Number(match[1]);
  const minutes = match[2] != null ? Number(match[2]) : 0;
  const meridiem = match[3] ? match[3].toLowerCase() : null;

  if (Number.isNaN(hours) || Number.isNaN(minutes) || minutes > 59) return null;

  if (meridiem === 'pm' && hours < 12) hours += 12;
  if (meridiem === 'am' && hours === 12) hours = 0;
  if (!meridiem && hours > 23) return null;

  return hours * 60 + minutes;
}

/**
 * Parses day hours like "9-17:30", "9:30-18:30", "9:00-12:00,13:00-17:30".
 * Blank / "closed" => no periods.
 * @param {string | null | undefined} raw
 * @returns {StoreHoursPeriod[]}
 */
export function parseDayHours(raw) {
  if (raw == null) return [];
  const value = String(raw).trim();
  if (!value || /^closed$/i.test(value)) return [];

  /** @type {StoreHoursPeriod[]} */
  const periods = [];

  for (const part of value.split(',')) {
    const segment = part.trim();
    if (!segment) continue;
    const [openRaw, closeRaw] = segment.split('-').map((piece) => piece?.trim());
    const openMinutes = parseTimeToMinutes(openRaw);
    const closeMinutes = parseTimeToMinutes(closeRaw);
    if (openMinutes == null || closeMinutes == null || closeMinutes <= openMinutes) continue;
    periods.push({ openMinutes, closeMinutes });
  }

  return periods.sort((a, b) => a.openMinutes - b.openMinutes);
}

/**
 * @param {number} totalMinutes
 * @returns {string}
 */
function formatMinutes(totalMinutes) {
  const hours24 = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const meridiem = hours24 >= 12 ? 'PM' : 'AM';
  let hours12 = hours24 % 12;
  if (hours12 === 0) hours12 = 12;
  if (minutes === 0) return `${hours12}${meridiem}`;
  return `${hours12}:${String(minutes).padStart(2, '0')}${meridiem}`;
}

/**
 * Opening-hours menu copy, e.g. "9am to 5:30pm". Drops :00.
 * @param {number} totalMinutes
 * @returns {string}
 */
function formatClock(totalMinutes) {
  const hours24 = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const suffix = hours24 >= 12 ? 'pm' : 'am';
  let hours12 = hours24 % 12;
  if (hours12 === 0) hours12 = 12;
  if (minutes === 0) return `${hours12}${suffix}`;
  return `${hours12}:${String(minutes).padStart(2, '0')}${suffix}`;
}

/**
 * @param {string | null | undefined} raw
 * @returns {string}
 */
function formatStorefrontHours(raw) {
  const periods = parseDayHours(raw);
  if (!periods.length) return 'Closed';
  return periods
    .map((period) => `${formatClock(period.openMinutes)} to ${formatClock(period.closeMinutes)}`)
    .join(', ');
}

/**
 * @param {string} timezone
 * @param {Date} [date]
 * @returns {{ dayIndex: number, minutes: number, dateKey: string }}
 */
function getZonedParts(timezone, date = new Date()) {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const parts = formatter.formatToParts(date);
  /** @type {Record<string, string>} */
  const map = {};
  for (const part of parts) {
    if (part.type !== 'literal') map[part.type] = part.value;
  }

  const weekdayMap = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };

  const dayIndex = weekdayMap[/** @type {keyof typeof weekdayMap} */ (map.weekday)] ?? 0;
  const hours = Number(map.hour);
  const minutes = Number(map.minute);
  const dateKey = `${map.year}-${map.month}-${map.day}`;

  return {
    dayIndex,
    minutes: hours * 60 + minutes,
    dateKey,
  };
}

/**
 * @param {StoreLocationData} data
 * @returns {Array<{date: string, hours: string}>}
 */
function normalizeSpecialHours(data) {
  const raw = data.specialHours;
  if (raw == null || raw === '') return [];
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((entry) => entry && typeof entry.date === 'string')
      .map((entry) => ({
        date: entry.date,
        hours: entry.hours == null ? '' : String(entry.hours),
      }));
  } catch {
    return [];
  }
}

/**
 * @param {StoreLocationData} data
 * @param {number} dayIndex
 * @param {string} dateKey
 * @returns {StoreHoursPeriod[] | null} null when a special override closes the day with no periods
 */
function periodsForDate(data, dayIndex, dateKey) {
  const special = normalizeSpecialHours(data).find((entry) => entry.date === dateKey);
  if (special) {
    return parseDayHours(special.hours);
  }
  const key = DAY_KEYS[dayIndex];
  return parseDayHours(data.hours?.[key]);
}

/**
 * @typedef {Object} StoreHoursStatus
 * @property {boolean} isOpen
 * @property {string} message
 */

/**
 * @param {StoreLocationData} data
 * @param {Date} [now]
 * @returns {StoreHoursStatus}
 */
export function getStoreHoursStatus(data, now = new Date()) {
  const timezone = data.timezone?.trim() || 'Australia/Sydney';
  const { dayIndex, minutes, dateKey } = getZonedParts(timezone, now);
  const todayPeriods = periodsForDate(data, dayIndex, dateKey) ?? [];

  for (const period of todayPeriods) {
    if (minutes >= period.openMinutes && minutes < period.closeMinutes) {
      return {
        isOpen: true,
        message: `Open now. Closes ${formatMinutes(period.closeMinutes)} today`,
      };
    }
  }

  const laterToday = todayPeriods.find((period) => minutes < period.openMinutes);
  if (laterToday) {
    return {
      isOpen: false,
      message: `Closed now. Opens today at ${formatMinutes(laterToday.openMinutes)}`,
    };
  }

  for (let offset = 1; offset <= 7; offset += 1) {
    const probe = new Date(now.getTime() + offset * 24 * 60 * 60 * 1000);
    const zoned = getZonedParts(timezone, probe);
    const periods = periodsForDate(data, zoned.dayIndex, zoned.dateKey) ?? [];
    if (!periods.length) continue;

    const openLabel = formatMinutes(periods[0].openMinutes);
    if (offset === 1) {
      return {
        isOpen: false,
        message: `Closed now. Opens tomorrow at ${openLabel}`,
      };
    }

    return {
      isOpen: false,
      message: `Closed now. Opens ${DAY_LABELS[zoned.dayIndex]} at ${openLabel}`,
    };
  }

  const holidayNote = data.publicHolidayNote?.trim();
  return {
    isOpen: false,
    message: holidayNote ? `Closed now. ${holidayNote}` : 'Closed now',
  };
}

/**
 * Custom element: reads JSON from a child script[type="application/json"]
 * or from data-store-hours, then paints open/closed status.
 */
class DacStoreHoursStatus extends HTMLElement {
  /** @type {ReturnType<typeof setInterval> | null} */
  #intervalId = null;

  /** @type {((event: Event) => void) | null} */
  #sectionLoadListener = null;

  connectedCallback() {
    this.#render();
    this.#intervalId = setInterval(() => this.#render(), 60_000);

    if (typeof window.Shopify !== 'undefined' && window.Shopify.designMode) {
      this.#sectionLoadListener = () => this.#render();
      document.addEventListener('shopify:section:load', this.#sectionLoadListener);
    }
  }

  disconnectedCallback() {
    if (this.#intervalId != null) {
      clearInterval(this.#intervalId);
      this.#intervalId = null;
    }
    if (this.#sectionLoadListener) {
      document.removeEventListener('shopify:section:load', this.#sectionLoadListener);
      this.#sectionLoadListener = null;
    }
  }

  /**
   * @returns {StoreLocationData | null}
   */
  #readData() {
    const script = this.querySelector('script[type="application/json"]');
    const raw = script?.textContent?.trim() || this.dataset.storeHours || '';
    if (!raw || raw === 'null') return null;
    try {
      return /** @type {StoreLocationData} */ (JSON.parse(raw));
    } catch {
      return null;
    }
  }

  #render() {
    const data = this.#readData();
    const indicator = this.querySelector('[data-store-hours-indicator]');
    const openIcon = this.querySelector('[data-status-open]');
    const closedIcon = this.querySelector('[data-status-closed]');
    const message = this.querySelector('[data-store-hours-message]');
    if (!message) return;

    if (!data) {
      this.dataset.state = 'unavailable';
      if (indicator) indicator.hidden = true;
      message.textContent = this.dataset.fallbackText || '';
      return;
    }

    const status = getStoreHoursStatus(data);
    this.dataset.state = status.isOpen ? 'open' : 'closed';
    if (indicator) indicator.hidden = false;
    if (openIcon) openIcon.hidden = !status.isOpen;
    if (closedIcon) closedIcon.hidden = status.isOpen;
    message.textContent = status.message;
  }
}

if (!customElements.get('dac-store-hours-status')) {
  customElements.define('dac-store-hours-status', DacStoreHoursStatus);
}

/**
 * DAC customization: opening-hours dropdown.
 * Reads the Store hours JSON already rendered in this bar, fills each day,
 * highlights today, and keeps a mouse click from pinning the menu open.
 */
class DacOpeningHours extends HTMLElement {
  /** @type {HTMLDetailsElement | null} */
  #details = null;

  /** @type {HTMLElement | null} */
  #summary = null;

  /** @type {string} */
  #pointerType = '';

  /** @type {((event: PointerEvent) => void) | null} */
  #onPointerDown = null;

  /** @type {((event: MouseEvent) => void) | null} */
  #onSummaryClick = null;

  /** @type {((event: MouseEvent) => void) | null} */
  #onDocumentClick = null;

  /** @type {((event: KeyboardEvent) => void) | null} */
  #onKeyDown = null;

  /** @type {((event: FocusEvent) => void) | null} */
  #onFocusOut = null;

  connectedCallback() {
    // DAC customization: a second connect without a disconnect would stack listeners.
    this.#unbindDisclosure();
    this.#details = this.querySelector('details');
    this.#summary = this.querySelector('summary');
    this.#applySchedule();
    this.#bindDisclosure();
  }

  disconnectedCallback() {
    this.#unbindDisclosure();
  }

  #applySchedule() {
    const data = this.#readHoursData();
    const timezone = data?.timezone?.trim() || 'Australia/Sydney';
    const today = this.#todayKey(timezone);
    const rows = this.querySelectorAll('[data-day]');

    for (const row of rows) {
      const day = row.getAttribute('data-day');
      const time = row.querySelector('.dac-opening-hours__time');
      if (time) {
        time.textContent = data ? formatStorefrontHours(data.hours?.[day]) : '';
      }

      const isToday = day === today;
      row.classList.toggle('is-today', isToday);
      if (isToday) {
        row.setAttribute('aria-current', 'date');
      } else {
        row.removeAttribute('aria-current');
      }
    }
  }

  /**
   * @returns {StoreLocationData | null}
   */
  #readHoursData() {
    const script = this.closest('.dac-store-info-bar')?.querySelector(
      'dac-store-hours-status script[type="application/json"]'
    );
    const raw = script?.textContent?.trim();
    if (!raw || raw === 'null') return null;
    try {
      return /** @type {StoreLocationData} */ (JSON.parse(raw));
    } catch {
      return null;
    }
  }

  /**
   * @param {string} timezone
   * @returns {string}
   */
  #todayKey(timezone) {
    try {
      return DAY_KEYS[getZonedParts(timezone).dayIndex];
    } catch {
      return DAY_KEYS[getZonedParts('Australia/Sydney').dayIndex];
    }
  }

  #bindDisclosure() {
    const summary = this.#summary;
    const details = this.#details;
    if (!summary || !details) return;

    this.#onPointerDown = (event) => {
      this.#pointerType = event.pointerType;
    };

    this.#onSummaryClick = (event) => {
      const pointerType = this.#pointerType;
      this.#pointerType = '';
      const fineMouse =
        pointerType === 'mouse' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      if (!fineMouse) return;
      event.preventDefault();
    };

    this.#onDocumentClick = (event) => {
      if (!details.open) return;
      const target = event.target;
      if (target instanceof Node && this.contains(target)) return;
      details.open = false;
    };

    this.#onKeyDown = (event) => {
      if (event.key !== 'Escape' || !details.open) return;
      details.open = false;
      summary.focus();
    };

    this.#onFocusOut = () => {
      requestAnimationFrame(() => {
        if (!details.open) return;
        if (this.contains(document.activeElement)) return;
        details.open = false;
      });
    };

    summary.addEventListener('pointerdown', this.#onPointerDown);
    summary.addEventListener('click', this.#onSummaryClick);
    document.addEventListener('click', this.#onDocumentClick);
    this.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('focusout', this.#onFocusOut);
  }

  #unbindDisclosure() {
    if (this.#summary && this.#onPointerDown) {
      this.#summary.removeEventListener('pointerdown', this.#onPointerDown);
    }
    if (this.#summary && this.#onSummaryClick) {
      this.#summary.removeEventListener('click', this.#onSummaryClick);
    }
    if (this.#onDocumentClick) {
      document.removeEventListener('click', this.#onDocumentClick);
    }
    if (this.#onKeyDown) {
      this.removeEventListener('keydown', this.#onKeyDown);
    }
    if (this.#onFocusOut) {
      this.removeEventListener('focusout', this.#onFocusOut);
    }
    this.#onPointerDown = null;
    this.#onSummaryClick = null;
    this.#onDocumentClick = null;
    this.#onKeyDown = null;
    this.#onFocusOut = null;
  }
}

if (!customElements.get('dac-opening-hours')) {
  customElements.define('dac-opening-hours', DacOpeningHours);
}
